import { type Workout } from '@jackedlogs/shared';

import { collectSessions } from './entryHistoryPick';

// Pure best-e1RM math over an already-fetched history window, split out of
// useEntryHistory (CN-EHIST-SPLIT) so that file keeps room under the
// 500-line cap while the EQ-1 read-side work lands beside it. Same contract
// as ./entryHistoryPick: no React, no api, no module state — the caller
// owns the cache and passes the window in.

// Best e1RM (kg) for one slug across `workouts`, or null when the window
// holds no qualifying set for it. Best, not most-recent, so a sub-max set
// reads as "% of best" rather than a regression against an arbitrary last
// session. The in-progress (unsaved) workout is never part of `workouts`.
//
// The COLD-window policy (fall back to the snapshot persisted while online,
// LG-20) deliberately does NOT live here: only the caller knows whether its
// cache is cold, and with a live window in hand the online answer is
// authoritative. See `getBestE1rmForSlug` in ./useEntryHistory.
export function bestE1rmForSlug(
  workouts: Workout[],
  exerciseId: string,
): number | null {
  let best = 0;
  for (const session of collectSessions(workouts, exerciseId)) {
    if (session.topE1RM > best) best = session.topE1RM;
  }
  return best > 0 ? best : null;
}

// Best e1RM for every slug the window knows about — the map the offline
// snapshot persists.
//
// ⚠️ Deliberately NOT a second derivation of "best e1RM": it is DEFINED as
// `bestE1rmForSlug` applied slug by slug, so the offline number cannot drift
// from the online one for the same lift. Re-deriving it (say via
// `bestE1rmByExerciseInWindow`, which counts every entry per workout and
// applies a 4-week cutoff, where `collectSessions` takes the first entry per
// workout and no cutoff) would give two different answers for one lift.
// Do not "simplify" the two into one — and the guard is no longer only this
// comment: apps/mobile/__tests__/entryHistoryE1rmDerivation.test.ts goes red
// on a window whose best sits outside the 4-week cutoff, which is exactly the
// case a collapse would silently change.
//
// ⚠️ Naming trap: TimelineLoggerScreen.tsx declares a LOCAL callback also
// called `bestE1rmForSlug`, wrapping the cache-bound `getBestE1rmForSlug`.
// Importing this module's function into that file would shadow it silently.
export function bestE1rmBySlug(workouts: Workout[]): Record<string, number> {
  const out: Record<string, number> = {};
  const slugs = new Set<string>();
  for (const w of workouts) {
    for (const entry of w.entries) slugs.add(entry.exerciseId);
  }
  for (const slug of slugs) {
    const best = bestE1rmForSlug(workouts, slug);
    if (best !== null) out[slug] = best;
  }
  return out;
}
